import React from 'react';
import { twMerge } from 'tailwind-merge';

import { Box } from 'ui/box';
import { mergeSx } from 'ui/merge-sx';
import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import { Checkbox } from 'ui/checkbox';
import Tooltip from 'components/partials/tooltip/tooltip';
import ReportAssignmentUsers from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/report-assignment-users';
import Icons from 'assets/icons';

import { getBorderSx } from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/report-assignment.utils';
import { USER_ASSIGNMENT_STATUS } from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/report-assignment.constants';
import { useExpandable } from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/hooks/use-exandable';

import type {
  ReportData,
  ReportUser,
} from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/hooks/use-report-data';

interface UserAccessChangePayload {
  userId: string | number;
  hasAccess: boolean;
}

interface ReportAssignmentRowProps {
  report: ReportData;
  fetchReportUsers: (reportId: string | number) => Promise<ReportUser[] | null>;
  onReportAccessChange: (
    reportId: string | number,
    value: boolean
  ) => void | Promise<void>;
  onDataIssueChange: (
    reportId: string | number,
    value: boolean
  ) => void | Promise<void>;
  onAutoAssignChange: (
    reportId: string | number,
    value: boolean
  ) => void | Promise<void>;
  onUserAccessChange: (
    reportId: string | number,
    userData: UserAccessChangePayload
  ) => void;
  reportUsers: ReportUser[] | undefined;
  isFirst: boolean;
  isLast: boolean;
  isLoading: boolean;
  isInternalAdmin?: boolean;
}

const ReportAssignmentRow = ({
  report,
  fetchReportUsers,
  onReportAccessChange,
  onDataIssueChange,
  onAutoAssignChange,
  onUserAccessChange,
  reportUsers,
  isFirst,
  isLast,
  isLoading,
  isInternalAdmin = false,
}: ReportAssignmentRowProps): React.JSX.Element => {
  const { isExpanded, isPending, isClosing, toggle } = useExpandable(
    (reportId: string | number) => fetchReportUsers(reportId)
  );

  const handleToggle = (): void => {
    if (isLoading) return;
    toggle(report.id, !!reportUsers);
  };

  const borderSx = getBorderSx(isFirst, isLast);
  const isDisabled = isLoading || isInternalAdmin;
  const isRequired = !!report.requiredForAllUsers;
  const reportName = report.title?.trim() || `Report ${report.id}`;

  return (
    <>
      <tr className="transition-colors duration-200">
        <Box component="td" sx={mergeSx({ py: 1.5, width: '5%' }, borderSx)}>
          <Stack direction="row" sx={{ justifyContent: 'center' }}>
            <Button
              variant="reserve-text"
              size="xs"
              onClick={handleToggle}
              aria-label={isExpanded ? 'Collapse users' : 'Expand users'}
              iconBefore={
                <div
                  className={`transform transition-transform duration-300 text-navy-500 ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                >
                  {isExpanded ? (
                    <Icons.OutlinedMinus className="size-5" />
                  ) : (
                    <Icons.OutlinedPlus
                      className={twMerge(
                        'size-5',
                        isPending ? 'opacity-50' : ''
                      )}
                    />
                  )}
                </div>
              }
              className="p-0"
              disabled={isLoading || isPending}
            />
          </Stack>
        </Box>
        <Box
          component="td"
          sx={mergeSx({ py: 1.5, px: 2, width: '40%' }, borderSx)}
        >
          <span className="font-semibold text-gray-700">{report.title}</span>
        </Box>
        <Box
          component="td"
          sx={mergeSx(
            { py: 1.5, px: 2, textAlign: 'center', width: '15%' },
            borderSx
          )}
        >
          <Checkbox
            indeterminate={
              report.userAssignmentStatus ===
              USER_ASSIGNMENT_STATUS.PARTIALLY_ASSIGNED
            }
            id={`report-access-${report.id}`}
            value={String(report.id)}
            checked={
              report.userAssignmentStatus ===
              USER_ASSIGNMENT_STATUS.ALL_ASSIGNED
            }
            onChange={() =>
              onReportAccessChange(
                report.id,
                report.userAssignmentStatus !==
                  USER_ASSIGNMENT_STATUS.ALL_ASSIGNED
              )
            }
            disabled={isDisabled}
            size="small"
            aria-label={`Report access for ${reportName}`}
            data-testid={`checkbox-report-access-${report.id}`}
          />
        </Box>
        <Box
          component="td"
          sx={mergeSx(
            { py: 1.5, px: 2, textAlign: 'center', width: '20%' },
            borderSx
          )}
        >
          <Tooltip
            content="Required for all users"
            enabled={isRequired}
            position="top"
          >
            <Checkbox
              id={`auto-assign-${report.id}`}
              value={String(report.id)}
              checked={isRequired || !!report.autoAssignToUsers}
              onChange={() =>
                onAutoAssignChange(report.id, !report.autoAssignToUsers)
              }
              disabled={isDisabled || isRequired}
              size="small"
              data-testid={`checkbox-auto-assign-${report.id}`}
              aria-label={
                isRequired
                  ? `New user default for ${reportName}, required for all users`
                  : `New user default for ${reportName}`
              }
            />
          </Tooltip>
        </Box>
        <Box
          component="td"
          sx={mergeSx(
            { py: 1.5, px: 2, textAlign: 'center', width: '20%' },
            borderSx
          )}
        >
          <Checkbox
            id={`data-issue-${report.id}`}
            value={String(report.id)}
            checked={!!report.hasDataIssue}
            onChange={() => onDataIssueChange(report.id, !report.hasDataIssue)}
            disabled={isDisabled}
            size="small"
            aria-label={`Data issue visibility for ${reportName}`}
            data-testid={`checkbox-data-issue-${report.id}`}
          />
        </Box>
      </tr>

      {(isExpanded || isClosing) && reportUsers && (
        <ReportAssignmentUsers
          reportId={report.id}
          reportUsers={reportUsers}
          onUserAccessChange={onUserAccessChange}
          isDisabled={isDisabled}
          isClosing={isClosing}
        />
      )}
    </>
  );
};

export default React.memo(ReportAssignmentRow);
